import { useRequest } from 'ahooks';
import {
    FALLBACK_COMMON_FONT_FAMILIES,
    FALLBACK_SCRIPT_FONT_SPECS,
} from '@meitu/saas-editor-core/es/framework/services/font/fallback';
import { editor } from '@/instance';
import { fetchArtTextMaterials, fetchFontCategoryList, fetchListStyleMaterials } from '@/services/font';
import { normalizeFontMaterial } from '@/services/font/normalize-font-material';
import { transformArtTextMaterialRaw, transformListStyleMaterialRaw } from '@/services/font/transform';
import { fetchTextMark } from '@/services/material';
import { useFontMaterialModel } from '@/store/models/font-material-model';
import { getQuery } from '@/utils/query';
import { getCommonParams } from '@/utils/request';

/**
 * 在编辑器初始化组件中挂载，为模板加载准备字体目录、文字引擎和文字素材
 *
 * 依赖已挂载的编辑器、字体素材 store 和 URL 中的应用来源
 *
 * @returns 素材加载函数及各类素材的请求状态
 */
export const useFontMaterials = () => {
    const setFonts = useFontMaterialModel((state) => state.setFonts);
    const setListStyles = useFontMaterialModel((state) => state.setListStyles);
    const setArtTexts = useFontMaterialModel((state) => state.setArtTexts);
    const setHighlights = useFontMaterialModel((state) => state.setHighlights);
    const query = getQuery();
    const { loading, runAsync } = useRequest(fetchFontCategoryList, {
        manual: true,
    });

    const { loading: isLoadingListStyles, runAsync: fetchListStyles } = useRequest(fetchListStyleMaterials, {
        manual: true,
    });

    const { loading: isLoadingArtTexts, runAsync: fetchArtTexts } = useRequest(fetchArtTextMaterials, {
        manual: true,
    });

    const loadFontMaterials = async () => {
        try {
            const params = getCommonParams();
            const [data, listStyleList, artTextList, highlightList] = await Promise.all([
                runAsync({ ...params, app_src: query.app_src || '' }),
                fetchListStyles(),
                fetchArtTexts(),
                fetchTextMark(),
            ]);
            const fonts = data.flatMap(({ materials, category_id, category_name }) =>
                materials.map((material) => normalizeFontMaterial(material, { category_id, category_name })),
            );
            // app_src 会裁剪可选字体；引擎的回退字体需从完整目录补齐，不改变字体面板范围。
            const fallbackFamilies = new Set<string>([
                ...Object.values(FALLBACK_COMMON_FONT_FAMILIES),
                ...FALLBACK_SCRIPT_FONT_SPECS.map(({ fontFamily }) => fontFamily),
            ]);
            fonts.forEach(({ font_name }) => fallbackFamilies.delete(font_name));
            const fallbackCategories =
                query.app_src && fallbackFamilies.size > 0
                    ? await fetchFontCategoryList({ ...params, app_src: '' })
                    : [];
            const fallbackFonts = fallbackCategories.flatMap(({ materials, category_id, category_name }) =>
                materials
                    .filter(({ font_name }) => fallbackFamilies.has(font_name))
                    .map((material) => normalizeFontMaterial(material, { category_id, category_name })),
            );
            setFonts(fonts, fallbackFonts);
            setListStyles(listStyleList.map(transformListStyleMaterialRaw));
            setArtTexts(artTextList.map(transformArtTextMaterialRaw));
            setHighlights(highlightList);

            editor.services.font.setRaw([...fonts, ...fallbackFonts]);
            editor.services.textHighlight.setHighlights(highlightList);
            // 依赖 editor.mount()/create() 已触发 TextEngineService.onCreate（WASM 路径就绪）
            await editor.services.textEngine.initialize();
            // 无文字模板也需要可测量的引擎，避免首次添加文字时尚未注册任何字体。
            await editor.services.font.loadFallbacks([]);
        } catch (error) {
            // TODO: 处理错误
            console.log('初始化字体素材失败', error);
        }
    };

    return {
        loading,
        loadFontMaterials,
        isLoadingListStyles,
        isLoadingArtTexts,
    };
};
